import * as PATHS from '@/constants/path';
import { request } from '@/services/api/request';
import { GetCallsRequest } from '@/typings/api/calls';
import { URLParamBuilder } from '@/utils/functions';

export function enableOutlookForPartnerRequest({data}) {
  const url = URLParamBuilder(PATHS.INTEGRATION_OUTLOOK_PARTNER_ENABLE);
  return request<GetCallsRequest['response']>(url, {
    method: 'POST',
    data
  });
}

export function disableOutlookForPartnerRequest() {
  const url = URLParamBuilder(PATHS.INTEGRATION_OUTLOOK_PARTNER_DISABLE);
  return request<GetCallsRequest['response']>(url, {
    method: 'DELETE',
  });
}

export function connectOutlookUserRequest() {
  const url = URLParamBuilder(PATHS.INTEGRATION_OUTLOOK_USER_CONNECT);
  return request<any>(url, {
    method: 'POST',
  });
}

export function disconnectOutlookUserRequest() {
  const url = URLParamBuilder(PATHS.INTEGRATION_OUTLOOK_USER_DISCONNECT);
  return request<any>(url, {
    method: 'DELETE',
  });
}

export function enableGoogleForPartnerRequest({data}) {
  const url = URLParamBuilder(PATHS.INTEGRATION_GOOGLE_PARTNER_ENABLE);
  return request<GetCallsRequest['response']>(url, {
    method: 'POST',
    data
  });
}

export function disableGoogleForPartnerRequest() {
  const url = URLParamBuilder(PATHS.INTEGRATION_GOOGLE_PARTNER_DISABLE);
  return request<GetCallsRequest['response']>(url, {
    method: 'DELETE',
  });
}

export function connectGoogleUserRequest() {
  const url = URLParamBuilder(PATHS.INTEGRATION_GOOGLE_USER_CONNECT);
  return request<any>(url, {
    method: 'POST',
  });
}

export function disconnectGoogleUserRequest() {
  const url = URLParamBuilder(PATHS.INTEGRATION_GOOGLE_USER_DISCONNECT);
  return request<any>(url, {
    method: 'DELETE',
  });
}

export function editIntegrationConfigurationRequest({params, data}) {
  const url = URLParamBuilder(PATHS.INTEGRATION_CONFIGURATION_EDIT, {
    params
  });
  return request<any>(url, {
    method: 'PUT',
    data
  });
}

const EWS_INTEGRATION_TYPE = 'exchange_ews_calendar';
const OFFICE365_MAIL_INTEGRATION_TYPE = 'office365_mail';
const MICROSOFT_SSO_INTEGRATION_TYPE = 'microsoft_sso';

// microsoft_sso mirrors office365_mail: enable returns { auth_url } for the
// incremental-consent redirect (openid profile email only — never the mail
// application permissions); the partner_integrations row and the tenant
// binding are written in the backend consent callback.
export function enableMicrosoftSsoForPartnerRequest({data}: {data?: any} = {}) {
  const url = URLParamBuilder(PATHS.INTEGRATION_GENERIC_ENABLE, {
    params: { integrationType: MICROSOFT_SSO_INTEGRATION_TYPE },
  });
  return request<any>(url, {
    method: 'POST',
    data: data || {},
  });
}

export function disableMicrosoftSsoForPartnerRequest() {
  const url = URLParamBuilder(PATHS.INTEGRATION_GENERIC_DISABLE, {
    params: { integrationType: MICROSOFT_SSO_INTEGRATION_TYPE },
  });
  return request<any>(url, {
    method: 'DELETE',
  });
}

// The legacy GET /integrations list returns configuration: [] for types it
// doesn't know, so the panel reads its full configuration from Laravel.
export function fetchMicrosoftSsoIntegrationRequest() {
  const url = URLParamBuilder(PATHS.INTEGRATION_CONFIGURATION_EDIT, {
    params: { integrationType: MICROSOFT_SSO_INTEGRATION_TYPE },
  });
  return request<any>(url, {
    method: 'GET',
  });
}

// Tenant groups for the mapping UI (live via the consent-time Graph token
// when possible, else the consent snapshot; empty ⇒ manual object-id entry).
export function fetchMicrosoftSsoGroupsRequest() {
  return request<any>(`/integrations/${MICROSOFT_SSO_INTEGRATION_TYPE}/groups`, {
    method: 'GET',
  });
}

// PATCH (merge) — the panel only owns role_mappings/default_role_id; a full
// PUT would clobber the tenant binding the consent callback wrote.
export function patchMicrosoftSsoConfigurationRequest({data}: {data: any}) {
  const url = URLParamBuilder(PATHS.INTEGRATION_CONFIGURATION_EDIT, {
    params: { integrationType: MICROSOFT_SSO_INTEGRATION_TYPE },
  });
  return request<any>(url, {
    method: 'PATCH',
    data,
  });
}

// office365_mail uses the generic integration routes (like EWS). Enable
// returns { auth_url } for the Azure admin-consent redirect — the
// partner_integrations row is only written in the consent callback.
export function enableOffice365MailForPartnerRequest({data}: {data?: any} = {}) {
  const url = URLParamBuilder(PATHS.INTEGRATION_GENERIC_ENABLE, {
    params: { integrationType: OFFICE365_MAIL_INTEGRATION_TYPE },
  });
  return request<GetCallsRequest['response']>(url, {
    method: 'POST',
    data: data || {},
  });
}

export function disableOffice365MailForPartnerRequest() {
  const url = URLParamBuilder(PATHS.INTEGRATION_GENERIC_DISABLE, {
    params: { integrationType: OFFICE365_MAIL_INTEGRATION_TYPE },
  });
  return request<GetCallsRequest['response']>(url, {
    method: 'DELETE',
  });
}

// PATCH (merge) — a full PUT would race the mailbox-directory sync and clobber
// mailboxes it just added (same reasoning as patchEwsConfigurationRequest).
export function patchOffice365MailConfigurationRequest({data}: {data: any}) {
  const url = URLParamBuilder(PATHS.INTEGRATION_CONFIGURATION_EDIT, {
    params: { integrationType: OFFICE365_MAIL_INTEGRATION_TYPE },
  });
  return request<any>(url, {
    method: 'PATCH',
    data,
  });
}


export function enableEwsForPartnerRequest({data}) {
  const url = URLParamBuilder(PATHS.INTEGRATION_GENERIC_ENABLE, {
    params: { integrationType: EWS_INTEGRATION_TYPE },
  });
  return request<GetCallsRequest['response']>(url, {
    method: 'POST',
    data
  });
}

export function disableEwsForPartnerRequest() {
  const url = URLParamBuilder(PATHS.INTEGRATION_GENERIC_DISABLE, {
    params: { integrationType: EWS_INTEGRATION_TYPE },
  });
  return request<GetCallsRequest['response']>(url, {
    method: 'DELETE',
  });
}

export function testEwsConnectionRequest({data}) {
  const url = URLParamBuilder(PATHS.INTEGRATION_GENERIC_TEST_CONNECTION_PUBLIC, {
    params: { integrationType: EWS_INTEGRATION_TYPE },
  });
  return request<any>(url, {
    method: 'POST',
    data
  });
}

// PATCH (merge) so unspecified fields keep their stored values — credentials are never wiped.
export function patchEwsConfigurationRequest({data}) {
  const url = URLParamBuilder(PATHS.INTEGRATION_CONFIGURATION_EDIT, {
    params: { integrationType: EWS_INTEGRATION_TYPE },
  });
  return request<any>(url, {
    method: 'PATCH',
    data
  });
}
